import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import "tom-select/dist/css/tom-select.css";
import "../stylesheets/tomselect_tailwind.css";

// Connects to data-controller="tomselect"
export default class extends Controller {
  static values = {
    options: Object,
    placeholder: String,
  };

  connect() {
    this.initializeSelect();
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
    }
  }

  initializeSelect() {
    const customOpts = this.optionsValue || {};
    const isMultiple = this.element.multiple;
    const placeholder =
      customOpts.placeholder || this.placeholderValue || "Choose...";

    const plugins = {};

    // Enable remove_button for multi-selects so badges have an X
    if (isMultiple) {
      plugins.remove_button = { title: "Remove" };
    }

    const emptyOptions = Array.from(
      this.element.querySelectorAll('option[value=""]')
    );
    const labeledEmptyOptions = emptyOptions.filter(
      (opt) => opt.textContent.trim().length > 0
    );

    // Remove only truly blank placeholder rows; keep labeled empty options
    // like "All Teams" so users can intentionally clear a filter.
    emptyOptions
      .filter((opt) => opt.textContent.trim().length === 0)
      .forEach((opt) => opt.remove());

    const allowEmptyOption =
      customOpts.allowEmptyOption ?? labeledEmptyOptions.length > 0;

    this.tomSelect = new TomSelect(this.element, {
      plugins,
      placeholder,
      allowEmptyOption,
      closeAfterSelect: customOpts.closeOnSelect ?? !isMultiple,
      hidePlaceholder: true,
      controlInput: null, // No inline typing -- acts like a proper select
      render: {
        no_results: () =>
          '<div class="no-results">No results found</div>',
      },
    });

    // Inject a custom placeholder element since controlInput: null
    // removes the native input that Tom Select uses for placeholder text.
    this.placeholderEl = document.createElement("span");
    this.placeholderEl.className = "ts-custom-placeholder";
    this.placeholderEl.textContent = placeholder;
    this.tomSelect.control.prepend(this.placeholderEl);

    // When all options are selected, close the dropdown and update visual state
    this.tomSelect.on("change", () => {
      const remaining = Object.keys(this.tomSelect.options).length -
        this.tomSelect.items.length;
      if (remaining <= 0) {
        this.tomSelect.close();
      }
      this.#updateVisualState();
    });

    // Also update on dropdown open
    this.tomSelect.on("dropdown_open", () => {
      this.#updateVisualState();
    });

    this.#updateVisualState();
  }

  // Update placeholder visibility and exhausted-options state
  #updateVisualState() {
    if (!this.tomSelect) return;

    const hasItems = this.tomSelect.items.length > 0;
    const remaining = Object.keys(this.tomSelect.options).length -
      this.tomSelect.items.length;

    // Show placeholder only when nothing is selected
    if (this.placeholderEl) {
      this.placeholderEl.style.display = hasItems ? "none" : "";
    }

    // Dim caret when all options are used up
    this.tomSelect.wrapper.classList.toggle("all-selected", remaining <= 0);
  }

  handleChange(_event) {
    // Submit the form when selection changes
    const form = this.element.closest("form");
    if (form) {
      form.requestSubmit();
    }
  }

  // Allow external access to the TomSelect instance
  get select() {
    return this.tomSelect;
  }
}
